/**
 * Telemetria e alarmes dos dispositivos para o cadastro de ativos (US-4603, Sprint 46) — Nexora Grid (frontend).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Retrato de `GET /v1/tempo-real/ultimas` (o mesmo do CCO) e dos alarmes das últimas 24 h, relido a cada minuto.
 * Só leitura. Falha de uma das fontes não impede a lista: o ativo aparece sem a leitura.
 */
import { useEffect, useState } from "react";
import { api } from "../api";
import { montarTelemetria, type TelemetriaDispositivo } from "./regrasCadastro";

type Ultimas = { leituras: { device_id: string; medida: string; valor: number; timestamp: string }[]; dados_velhos: string[] };
type Alarmes = { grupos: { device_id: string; prioridade: 1 | 2 | 3 | 4; reconhecido?: boolean; descricao: string }[] };

export function useTelemetriaAtivos(empresa: string | null): Map<string, TelemetriaDispositivo> {
  const [mapa, setMapa] = useState<Map<string, TelemetriaDispositivo>>(new Map());
  useEffect(() => {
    if (!empresa) return;
    let vivo = true;
    const ler = async () => {
      const [ultimas, alarmes] = await Promise.all([
        api<Ultimas>(`/v1/tempo-real/ultimas?empresa=${empresa}`).catch(() => ({ leituras: [], dados_velhos: [] }) as Ultimas),
        api<Alarmes>(`/v1/alarmes?empresa=${empresa}&horas=24`).catch(() => ({ grupos: [] }) as Alarmes),
      ]);
      if (vivo) setMapa(montarTelemetria(ultimas.leituras ?? [], ultimas.dados_velhos ?? [], alarmes.grupos ?? []));
    };
    void ler();
    const id = window.setInterval(() => void ler(), 60_000);
    return () => { vivo = false; window.clearInterval(id); };
  }, [empresa]);
  return mapa;
}

/** Série de 24 h de uma medida (para a aba Telemetria da ficha). */
export async function serie24h(empresa: string, dispositivo: string, medida: string): Promise<[string, number][]> {
  const r = await api<{ series: Record<string, [string, number][]> }>(
    `/v1/tempo-real/historico?empresa=${empresa}&dispositivo=${encodeURIComponent(dispositivo)}&medidas=${medida}&minutos=1440`);
  return r.series?.[medida] ?? [];
}
